import React, { useMemo } from 'react'
import { Flex, Box, Text } from '@clear/design-system'
import { ColorType } from '@clear/design-system/lib/theme/colors'
import Icon from '@components/shared/Icon'
import { getEnrollSteps, RENEWAL_STEPS } from './steps'
import usePNEL from '@hooks/usePNEL'

type Step = {
  name: string
}
export const stepperMode = {
  renewal: 'renewal',
  enroll: 'enroll',
} as const
export interface StepperProps {
  current: number
  mode: keyof typeof stepperMode
}

const blue = 'dhsBlue' as ColorType
const grey = 'stepperGrey' as ColorType

const StepperCircle = ({
  name,
  color,
  index,
  current,
  isCurrent,
  total,
  enableNodes,
}: {
  icon?: JSX.Element
  color: ColorType
  name: string
  index: number
  current: number
  total: number
  isCurrent: boolean
  enableNodes: boolean
}) => {
  const circleSize = () => {
    if (!enableNodes) {
      return '0px'
    }

    if (current === 0) {
      return index === 0 ? '24px' : '0px'
    }
    if (index < current) {
      return '24px'
    }

    if (index === current) {
      return '38px'
    }
    return '0px'
  }

  return (
    <Flex alignItems="center" flexDirection="column" width="0px" flexShrink={0}>
      <Box
        height="38px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {enableNodes && index === total - 1 && current === total ? (
          <Icon
            name="stepper-check"
            alt="check"
            size={38}
            className="xs:mb-4 mb-3"
          />
        ) : (
          <Flex
            height={circleSize()}
            mb={[4, 3]}
            width={circleSize()}
            zIndex={1}
            backgroundColor={color}
            alignItems="center"
            justifyContent="center"
            data-testid={`stepper-circle-${index}`}
            borderRadius="100%"
          />
        )}
      </Box>

      <Box display={['none', 'flex']} mt="4px" height="20px">
        <Text
          color="neutralsTextBlack"
          fontSize={2}
          data-testid={`stepper-text-${isCurrent ? 'current' : `${index}`}`}
          sx={{
            whiteSpace: 'nowrap',
          }}
        >
          {isCurrent ? <strong>{name}</strong> : name}
        </Text>
      </Box>
    </Flex>
  )
}

const Spacer = ({
  index,
  current,
  isFirst = false,
  isLast = false,
}: {
  color?: ColorType
  index: number
  current: number
  isFirst: boolean
  isLast: boolean
}) => (
  <Flex
    width="100%"
    height={['0.5rem', '0.75rem']}
    mt={[2, 2]}
    backgroundColor={index < current ? blue : grey}
    borderBottomLeftRadius={isFirst ? '8px' : '0px'}
    borderTopLeftRadius={isFirst ? '8px' : '0px'}
    borderBottomRightRadius={isLast ? '8px' : '0px'}
    borderTopRightRadius={isLast ? '8px' : '0px'}
  />
)

export const Stepper = ({ current, mode }: StepperProps) => {
  const { isEnabledOnWebsite: isPNELEnabled } = usePNEL()
  const steps = useMemo(() => {
    if (mode === 'renewal') {
      return RENEWAL_STEPS
    }
    return getEnrollSteps(isPNELEnabled)
  }, [mode, isPNELEnabled])

  return (
    <Box width="100%" background="#E8EAEC" py={2}>
      <Flex
        maxWidth="80%"
        width={['full', 'tablet', 'content']}
        marginX="auto"
        my={5}
      >
        {steps.map(({ name }: Step, index: number) => {
          const color: ColorType = index <= current ? blue : grey

          return (
            <React.Fragment key={`stepper-${index}`}>
              <StepperCircle
                color={color}
                name={name}
                current={current}
                index={index}
                total={steps.length}
                isCurrent={current === index}
                enableNodes={!isPNELEnabled}
              />

              {index < steps.length - 1 ? (
                <Spacer
                  color={color}
                  index={index}
                  current={current}
                  isFirst={index === 0}
                  isLast={index === steps.length - 2}
                />
              ) : null}
            </React.Fragment>
          )
        })}
      </Flex>
    </Box>
  )
}
Stepper.displayName = 'Stepper'
export default Stepper
